import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useSupabaseContext } from "../lib/supabase_context";
import { useToast } from "../hooks/use-toast";

export interface CartItem {
  id: string | number;
  product_id: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
  store_id: number | string;
  store_name: string;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => Promise<void>;
  removeFromCart: (id: string | number) => Promise<void>;
  updateQuantity: (id: string | number, quantity: number) => Promise<void>;
  loadCart: () => Promise<void>;
  /** False when the signed-in user is on the store team for this store. */
  canAddStoreProduct: (storeId: string | number | null | undefined) => boolean;
  isLoading: boolean;
  totalItems: number;
  totalPrice: number;
  showCartPopup: boolean;
  setShowCartPopup: (v: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// API Wrapper
async function fetchCartAPI(action: string, payload?: Record<string, unknown>) {
  try {
    const res = await fetch("/api/cart", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, payload }),
    });

    const data = await res.json().catch(() => null);

    if (!res.ok) {
      return {
        error: true,
        details: data?.error || data?.details || "Request failed",
      };
    }

    return data;
  } catch (e) {
    console.error("Cart API error:", e);
    return { error: true, details: "Network error" };
  }
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { session, supabase } = useSupabaseContext();
  const userId = session?.user?.id ?? null;
  const { toast } = useToast();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [adminStoreIds, setAdminStoreIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [showCartPopup, setShowCartPopup] = useState(false);

  const canAddStoreProduct = useCallback(
    (storeId: string | number | null | undefined) => {
      if (!userId || storeId == null || storeId === "") return true;
      return !adminStoreIds.has(String(storeId));
    },
    [userId, adminStoreIds],
  );

  // Load guest cart
  const loadGuestCart = useCallback(async (): Promise<CartItem[]> => {
    const data = await fetchCartAPI("load");
    if (data.error) return [];
    return data.items || [];
  }, []);

  // Load user or guest cart
  const loadCart = useCallback(async () => {
    setIsLoading(true);

    if (!userId) {
      const items = await loadGuestCart();
      setCartItems(items);
      setIsLoading(false);
      return;
    }

    const data = await fetchCartAPI("load");

    if (data.error) {
      toast({
        title: "Error loading cart",
        description: "Please try again later.",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    setCartItems(data.items || []);
    setIsLoading(false);
  }, [userId, loadGuestCart, toast]);

  // Update quantity
  const updateQuantity = useCallback(
    async (id: string | number, quantity: number) => {
      const payload = { id, quantity };

      const data = await fetchCartAPI("update", payload);

      if (data.error) {
        toast({
          title: "Update failed",
          description: "Please try again.",
          variant: "destructive",
        });
        return;
      }

      setCartItems((prev) => prev.map((i) => (i.id === id ? data.item : i)));
    },
    [toast]
  );

  // Add to cart
  const addToCart = useCallback(
    async (item: CartItem) => {
      if (!canAddStoreProduct(item.store_id)) {
        toast({
          title: "Cannot add to cart",
          description:
            "Store team members cannot add products from their own store to the cart.",
          variant: "destructive",
        });
        return;
      }

      const existing = cartItems.find((i) => i.product_id === item.product_id);

      if (existing) {
        await updateQuantity(existing.id, existing.quantity + item.quantity);
        setShowCartPopup(true);
        return;
      }

      const payload = item as unknown as Record<string, unknown>;

      const data = await fetchCartAPI("add", payload);

      if (data.error) {
        toast({
          title: "Error adding item",
          description: data.details || data.error || "Try again later.",
          variant: "destructive",
        });
        return;
      }

      // Check if item already exists in state (might exist in DB but not loaded yet)
      setCartItems((prev) => {
        const existingIndex = prev.findIndex(
          (i) => i.product_id === data.item.product_id
        );
        if (existingIndex >= 0) {
          // Update existing item
          const updated = [...prev];
          updated[existingIndex] = data.item;
          return updated;
        }
        // Add new item
        return [...prev, data.item];
      });
      setShowCartPopup(true);
    },
    [cartItems, updateQuantity, toast, canAddStoreProduct]
  );

  // Clear cart
  const clearCart = useCallback(async () => {
    await fetchCartAPI("clear");
    setCartItems([]);
  }, []);

  // Remove from cart
  const removeFromCart = useCallback(
    async (id: string | number) => {
      const data = await fetchCartAPI("remove", { id });

      if (data.error) {
        toast({
          title: "Error removing item",
          description: "Try again later.",
          variant: "destructive",
        });
        return;
      }

      const updated = cartItems.filter((i) => i.id !== id);
      setCartItems(updated);

      if (updated.length === 0) {
        await clearCart();
      }
    },
    [cartItems, clearCart, toast]
  );

  // Migrate guest cart → user cart
  const migrateGuestCartToSupabase = useCallback(
    async () => {
      const data = await fetchCartAPI("migrate");

      if (data.error) {
        console.error("Migration error:", data.error);
        return;
      }

      console.log("Migration successful");
    },
    []
  );

  // Stores where the signed-in user is on the admin team (cannot buy as a customer).
  useEffect(() => {
    if (!userId) {
      setAdminStoreIds(new Set());
      return;
    }

    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from("Stores")
        .select("id")
        .contains("admin_id", [userId]);

      if (cancelled) return;

      if (error) {
        console.error("Failed to load store admin memberships:", error);
        setAdminStoreIds(new Set());
        return;
      }

      setAdminStoreIds(new Set((data ?? []).map((row) => String(row.id))));
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, supabase]);

  // Initial cart load + auth listener for keeping guest/user cart in sync.
  useEffect(() => {
      loadCart().catch((error) => {
        console.error("Initial cart load failed:", error);
      });

    const { data: listener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try{
          const newUserId = session?.user?.id ?? null;
  
          if (event === "SIGNED_IN" && newUserId) {
            await migrateGuestCartToSupabase();
          }
  
           // Reload cart after auth changes so guest/user cart stays in sync.
          await loadCart();

        }catch(e){
          console.error("Auth state change error:", e);
        }
      }
    );

    return () => listener.subscription.unsubscribe();
  }, [supabase, migrateGuestCartToSupabase, loadCart]);

  const totalItems = cartItems.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = cartItems.reduce((s, i) => s + i.quantity * i.price, 0);

  const value = useMemo(
    () => ({
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      loadCart,
      canAddStoreProduct,
      totalItems,
      totalPrice,
      isLoading,
      showCartPopup,
      setShowCartPopup,
    }),
    [
      cartItems,
      totalItems,
      totalPrice,
      isLoading,
      showCartPopup,
      addToCart,
      removeFromCart,
      updateQuantity,
      loadCart,
      canAddStoreProduct,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};